import React, { useCallback, useMemo } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { MediaImage } from "@app/scripts/react-general-components/MediaImage";
import { useIsMobile } from "@app/scripts/react-hooks/isMobile";
import { useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { ProductSizeSelect } from "@app/scripts/react-product-details/components/ProductSizeSelect";
import { ProductPrice } from "@app/scripts/react-product/components/ProductPrice";

import type { NormalisedProduct } from "@app/scripts/types";

type Props = {
  product: NormalisedProduct;
  toggleOpen?: () => void;
};

export const ProductStlProduct: React.FC<Props> = ({ product, toggleOpen }) => {
  const { error, handleAddToCart, handleChangeVariant, selectedVariantId, color, url, title } = useShopifyProduct({
    product,
  });
  const isMobile = useIsMobile();

  const addToCart = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      const success = await handleAddToCart(e);
      if (success) {
        toggleOpen?.();
      }
    },
    [handleAddToCart]
  );

  const firstMediaImg = useMemo(() => product?.media?.find((x) => !!x.src), [product?.media]);

  const submitTitle = isMobile ? "Add" : "Add To Cart";
  const sizeTitle = isMobile ? "Size" : "Select Size";

  return (
    <div className="product-stl__product">
      <div className="product-stl__product-image">{firstMediaImg && <MediaImage media={firstMediaImg} />}</div>

      <div className="product-stl__product-details">
        <div className="product-stl__product-title">
          <h2 className="text-lg font-normal">{title}</h2>
          {color && <span className="text-sm text-neutral-secondary-grey">{color}</span>}
          <ProductPrice product={product} />
        </div>

        <form method="post" action="/cart/add" onSubmit={addToCart} className="product-stl__product-form">
          <input name="id" value={selectedVariantId} type="hidden" />
          <ProductSizeSelect
            variants={product.variants}
            handleChangeVariant={handleChangeVariant}
            defaultTitle={sizeTitle}
          />
          <Button type="submit" label={submitTitle} variant="secondary" className="add-to-cart" />
        </form>

        {error && <small className="form__message text-brand-error pt-2">{error}</small>}

        <Button className="product-stl__product-view-details" href={url} variant="text" label="View Details" />
      </div>
    </div>
  );
};
